/* =============================================================================
   CORE / ORDER-STATUSES — geteilte Status-Klassifizierung für Counter & Filter
   =============================================================================
   v0.31.2 / Session 22 Nachschlag / Phase 22 (Funktionstest-Bug:
   Counter-Inkonsistenz)
   ----------------------------------------------------------------------------
   Bisher hatte jede Counter-Stelle ihre eigene Status-Filter-Logik:

     - `src/views/admin/orders.js`           → totalOpen = incoming + preparing + ready
     - `src/views/admin/orders-notifications.js` → openCount = [pending, preparing]
     - `src/views/admin/dashboard.js`        → openCount = [pending,confirmed,preparing,ready]

   Drei Stellen, drei verschiedene Definitionen von „offen" → Anzeigen
   weichen voneinander ab. Funktionstest S22: eine Order in `preparing`
   (KDS/Bar arbeitet) wurde im Cockpit-Toolbar als „1 offen" angezeigt,
   obwohl sie für den Bediener im Cockpit gerade NICHT auf eine Aktion
   wartet.

   Dieses Modul ist die geteilte Quelle der Wahrheit. Alle Counter,
   Filter, Tab-Pillen und Dashboard-Widgets MÜSSEN von hier kommen.
   Wenn neue Status-Buckets nötig sind, gehören sie hier rein — nicht
   in eine private Filter-Funktion irgendwo im View-Code.
============================================================================ */

/**
 * Order-Status-Klassifizierung — Bediener-Perspektive:
 *
 *   pending     → NEU: Order ist abgeschickt, Bediener muss annehmen.
 *   confirmed   → IN PIPELINE: angenommen, KDS+Bar haben pending Sub-Status.
 *   preparing   → IN PIPELINE: KDS oder Bar (oder beide) arbeitet.
 *   ready       → BEREIT: Master-Aggregation sagt fertig, Bediener muss ausgeben.
 *   picked_up   → ABGESCHLOSSEN (Bediener-finale Aktion).
 *   cancelled   → ABGESCHLOSSEN (Storno).
 *   no_show     → ABGESCHLOSSEN (Kunde kam nicht).
 */

const STATUS_NEW              = new Set(['pending']);
const STATUS_IN_PIPELINE      = new Set(['confirmed', 'preparing']);
const STATUS_READY            = new Set(['ready']);
const STATUS_AWAITING_OPERATOR = new Set(['pending', 'ready']);
const STATUS_ACTIVE           = new Set(['pending', 'confirmed', 'preparing', 'ready']);
const STATUS_FINISHED         = new Set(['picked_up', 'cancelled', 'no_show']);

/** Hilfs-Funktion: liest Status aus Order-Objekt unabhängig vom snake/camelCase-Pfad. */
function _statusOf(order) {
  if (!order) return '';
  return order.status || order._status || '';
}

/** Neu / wartet auf Annahme durch den Bediener. */
export function isOrderNew(order) {
  return STATUS_NEW.has(_statusOf(order));
}

/** Bestellung läuft durch KDS/Bar (Bediener muss nichts tun, außer beobachten). */
export function isOrderInPipeline(order) {
  return STATUS_IN_PIPELINE.has(_statusOf(order));
}

/** Beide Channels fertig — Bediener muss ausgeben / abgeholt markieren. */
export function isOrderReady(order) {
  return STATUS_READY.has(_statusOf(order));
}

/**
 * Bediener-relevant: pending (annehmen) oder ready (ausgeben). Das hier ist
 * der „echte" offen-aus-Bediener-Sicht-Counter — alles dazwischen läuft
 * autonom durch KDS+Bar.
 */
export function isOrderAwaitingOperator(order) {
  return STATUS_AWAITING_OPERATOR.has(_statusOf(order));
}

/** Alle non-finished Orders (auch wenn nicht Bediener-aktion-pflichtig). */
export function isOrderActive(order) {
  return STATUS_ACTIVE.has(_statusOf(order));
}

/** Abgeschlossen (picked_up, cancelled, no_show). REIN Status-basiert — prüft
 *  NICHT „bezahlt". Für die echte „erledigt"-Logik siehe isOrderSettled. */
export function isOrderFinished(order) {
  return STATUS_FINISHED.has(_statusOf(order));
}

/**
 * S115 — Geteilte „echte erledigt"-Prädikate (Counter-Single-Source). Die
 * „Kassieren"-Spalte (picked_up-UNBEZAHLT, S60 FIX.5) ist noch eine offene
 * Bediener-Aufgabe → erst Zahlung macht eine Order endgültig fertig. Diese
 * Helfer lebten bisher inline in orders.js (getActiveOrders/getFinishedToday)
 * — hier extrahiert, damit Cockpit-Kanban UND Dashboard-Timeline dieselbe
 * Wahrheit nutzen. camel+snake-Lookup (Cockpit camelCase, DB-Rohzeile snake).
 */
export function isOrderPaid(order) {
  if (!order) return false;
  const p = order.paymentStatus ?? order.payment_status;
  return p === 'paid';
}

/** Kassiert: picked_up UND bezahlt. Storno/No-Show NICHT (eigene Bedeutung). */
export function isOrderCashedOut(order) {
  return _statusOf(order) === 'picked_up' && isOrderPaid(order);
}

/** Vollständig erledigt (= orders.js getFinishedToday Z.183): kassiert ODER
 *  storniert/no_show. picked_up-UNBEZAHLT zählt NICHT (Spalte „Kassieren"). */
export function isOrderSettled(order) {
  const s = _statusOf(order);
  return (s === 'picked_up' && isOrderPaid(order)) || s === 'cancelled' || s === 'no_show';
}

/**
 * Storno-Härtung (S75): Eine Bestellung ist „storno-gesperrt", sobald Essen
 * oder Getränk in Zubereitung ist (kitchen_status/bar_status preparing|ready)
 * oder der Fahrer unterwegs/zugestellt hat (delivery_status out_for_delivery|
 * delivered). Spiegelt die DB-Gates in cancel_order_by_token (phase_74).
 * camel+snake-Lookup, weil Cockpit-Objekte camelCase und DB-Rohzeilen snake
 * liefern können. Pure — testbar ohne DOM.
 */
const STORNO_KITCHEN_ACTIVE  = new Set(['preparing', 'ready']);
const STORNO_DELIVERY_ACTIVE = new Set(['out_for_delivery', 'delivered']);

function _subStatus(order, snake, camel) {
  if (!order) return '';
  return order[camel] ?? order[snake] ?? '';
}

/** True, wenn ein Customer-Self-Storno gesperrt sein muss (Küche/Bar/Lieferung aktiv). */
export function isStornoLocked(order) {
  if (!order) return false;
  return STORNO_KITCHEN_ACTIVE.has(_subStatus(order, 'kitchen_status', 'kitchenStatus'))
      || STORNO_KITCHEN_ACTIVE.has(_subStatus(order, 'bar_status', 'barStatus'))
      || STORNO_DELIVERY_ACTIVE.has(_subStatus(order, 'delivery_status', 'deliveryStatus'));
}

/**
 * S143 / Ticket 2 — True, wenn bereits **zubereitete Ware** existiert (Küche ODER
 * Theke preparing|ready). Trigger für die GoBD-Schwund-Buchung bei Storno/No-Show:
 * zubereitete Ware darf das System nicht ohne `order_corrections` (Warenverlust)
 * verlassen. Bewusst NUR Küche/Theke — der Liefer-Lebenszyklus terminiert im
 * Fahrer-Cockpit, nicht über diesen Pfad. Pure — testbar ohne DOM.
 */
export function hasPreparedGoods(order) {
  if (!order) return false;
  return STORNO_KITCHEN_ACTIVE.has(_subStatus(order, 'kitchen_status', 'kitchenStatus'))
      || STORNO_KITCHEN_ACTIVE.has(_subStatus(order, 'bar_status', 'barStatus'));
}

/**
 * Zählt die Orders in einer Liste nach Buckets, gemäß den Helpers oben.
 * Liefert `{ new, inPipeline, ready, awaitingOperator, active }`. Jeder
 * Counter-User holt sich daraus, was er anzeigen will.
 */
export function countOrdersByBucket(orders) {
  const out = { new: 0, inPipeline: 0, ready: 0, awaitingOperator: 0, active: 0 };
  if (!Array.isArray(orders)) return out;
  for (const o of orders) {
    if (isOrderNew(o))         out.new++;
    if (isOrderInPipeline(o))  out.inPipeline++;
    if (isOrderReady(o))       out.ready++;
    if (isOrderAwaitingOperator(o)) out.awaitingOperator++;
    if (isOrderActive(o))      out.active++;
  }
  return out;
}

// Backward-Compat: window-Zugriff für inline-Handler in Cockpit-UI
if (typeof window !== 'undefined') {
  window.OrderStatuses = {
    isOrderNew,
    isOrderInPipeline,
    isOrderReady,
    isOrderAwaitingOperator,
    isOrderActive,
    isOrderFinished,
    isOrderPaid,
    isOrderCashedOut,
    isOrderSettled,
    isStornoLocked,
    hasPreparedGoods,
    countOrdersByBucket
  };
}
